/**
 * ContentCreator — hub for organic content creators.
 * Tabs: AI Content (chat) | Graphics | Library | Pipeline | Video Edit
 * AI Content absorbs: Reel Creator, YouTube Mode, Idea Bank
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  MessageSquare,
  Image,
  BookImage,
  Workflow,
  Film,
  Youtube,
  Lightbulb,
  PenLine,
  CalendarDays,
  Scissors,
  Wand2,
  Sparkles,
  LayoutGrid,
  Layers,
  Inbox,
  BarChart3,
  Bot,
} from "lucide-react";
import YoutubeHighlight from "@/components/youtube-highlight/YoutubeHighlight";
import AdGraphicsBuilder from "@/components/ad-graphics-builder";
import GraphicsLibrary from "@/components/graphics-library";
import ContentPipeline from "@/components/content-pipeline";
import ContentScheduler from "@/components/content-scheduler";
import SchedulerCalendar from "@/components/scheduler/SchedulerCalendar";
import VideoEditor from "@/components/video-editor/video-editor";
import VideoGenerator from "@/components/video-generator/video-generator";
import MotionGraphicsStudio from "@/components/motion-graphics-studio";
import SocialInbox from "@/components/social-inbox";
import SocialAutomations from "@/components/social-automations";
import ContentAnalytics from "@/components/content-analytics";
import ChatInterface from "@/components/chat-interface";
import CarouselSidePanel from "@/components/carousel-creator/carousel-side-panel";
import DailyViralInspiracje from "@/components/daily-viral/DailyViralInspiracje";
import { useStore } from "@/lib/store";
import { readViewMode } from "@/lib/view-mode";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { ModuleErrorBoundary } from "@/components/ui/module-error-boundary";

type ContentTab =
  | "chat"
  | "graphics"
  | "library"
  | "pipeline"
  | "scheduler"
  | "calendar"
  | "montaz"
  | "generator"
  | "motion-graphics"
  | "inbox"
  | "automations"
  | "analytics"
  | "inspiracje"
  | "highlights";

type ChatSubModule =
  | "content-creator"
  | "reel-creator"
  | "youtube-mode"
  | "carousel-creator"
  | "stories-creator";

const CHAT_SUB_MODULES: { id: ChatSubModule; labelKey: string; icon: React.ReactNode }[] = [
  {
    id: "content-creator",
    labelKey: "contentCreator.subModule.general",
    icon: <PenLine size={12} />,
  },
  { id: "reel-creator", labelKey: "contentCreator.subModule.reels", icon: <Film size={12} /> },
  {
    id: "stories-creator",
    labelKey: "contentCreator.subModule.stories",
    icon: <Layers size={12} />,
  },
  {
    id: "carousel-creator",
    labelKey: "contentCreator.subModule.carousel",
    icon: <LayoutGrid size={12} />,
  },
  { id: "youtube-mode", labelKey: "contentCreator.subModule.youtube", icon: <Youtube size={12} /> },
];

const CONTENT_TAB_MODULES = new Set<string>([
  "content-creator",
  "reel-creator",
  "youtube-mode",
  "carousel-creator",
  "stories-creator",
]);

// Order: AI/idea creation → asset production → review/approval → distribution → analysis → tools.
// Reorganizacja zgodnie z transcript 2026-05: "wszystko na górze najnana".
const TABS: { id: ContentTab; labelKey: string; icon: React.ReactNode }[] = [
  { id: "chat", labelKey: "contentCreator.tab.chat", icon: <MessageSquare size={14} /> },
  { id: "graphics", labelKey: "contentCreator.tab.graphics", icon: <Image size={14} /> },
  { id: "library", labelKey: "contentCreator.tab.library", icon: <BookImage size={14} /> },
  { id: "pipeline", labelKey: "contentCreator.tab.pipeline", icon: <Workflow size={14} /> },
  { id: "calendar", labelKey: "contentCreator.tab.calendar", icon: <CalendarDays size={14} /> },
  { id: "scheduler", labelKey: "contentCreator.tab.scheduler", icon: <CalendarDays size={14} /> },
  { id: "analytics", labelKey: "contentCreator.tab.analytics", icon: <BarChart3 size={14} /> },
  { id: "inbox", labelKey: "contentCreator.tab.inbox", icon: <Inbox size={14} /> },
  { id: "automations", labelKey: "contentCreator.tab.automations", icon: <Bot size={14} /> },
  { id: "montaz", labelKey: "contentCreator.tab.montage", icon: <Scissors size={14} /> },
  { id: "generator", labelKey: "contentCreator.tab.generator", icon: <Wand2 size={14} /> },
  {
    id: "motion-graphics",
    labelKey: "contentCreator.tab.motionGraphics",
    icon: <Sparkles size={14} />,
  },
  { id: "highlights", labelKey: "contentCreator.tab.highlights", icon: <Scissors size={14} /> },
];

const SUPER_ADMIN_ONLY_TABS = new Set<ContentTab>([
  "generator",
  "motion-graphics",
  "montaz",
  "inbox",
  "highlights",
]);

export default function ContentCreator() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    currentModule,
    setCurrentModule,
    setCurrentChatId,
    selectedClientUserId,
    organicTab,
    setOrganicTab,
  } = useStore();
  const forUserId = selectedClientUserId ?? undefined;
  const [, setLocation] = useLocation();
  const [pendingGraphicPrompt, setPendingGraphicPrompt] = useState<string | undefined>(undefined);
  const [overrideTab, setOverrideTab] = useState<ContentTab | null>(null);

  // Deep-link from Viral Gallery "Recreate": ?tab=graphics&prompt=…&model=…
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const promptParam = params.get("prompt");
    const tabParam = params.get("tab");
    if (promptParam) setPendingGraphicPrompt(promptParam);
    const VALID_TABS: ContentTab[] = [
      "chat",
      "graphics",
      "library",
      "pipeline",
      "scheduler",
      "montaz",
      "generator",
      "motion-graphics",
      "inbox",
      "automations",
      "analytics",
      "inspiracje",
      "highlights",
    ];
    if (tabParam && (VALID_TABS as string[]).includes(tabParam)) {
      setOverrideTab(tabParam as ContentTab);
    }
    if (promptParam || tabParam) {
      // Scrub the prefill params so a refresh doesn't re-trigger them — but KEEP `job`,
      // which AdGraphicsBuilder reads on mount to reattach to a background generation.
      const url = new URL(window.location.href);
      url.searchParams.delete("prompt");
      url.searchParams.delete("tab");
      url.searchParams.delete("model");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // organicTab (set by sidebar in ORGANIC view) takes priority over URL-deep-link override.
  const VALID_TABS = new Set<string>([
    "chat",
    "graphics",
    "library",
    "pipeline",
    "scheduler",
    "montaz",
    "generator",
    "motion-graphics",
    "inbox",
    "automations",
    "analytics",
    "inspiracje",
    "highlights",
  ]);
  const organicTabValid =
    organicTab && VALID_TABS.has(organicTab) ? (organicTab as ContentTab) : null;
  const requestedTab: ContentTab =
    organicTabValid ??
    overrideTab ??
    (CONTENT_TAB_MODULES.has(currentModule) ? "chat" : "graphics");
  // Defense-in-depth: if a non-super-admin user lands on a super-admin-only tab
  // (e.g. via a saved URL or shared link), silently fall back to 'chat'.
  // The sidebar/tab bar already hide these tabs from the UI; this catches
  // direct URL navigation.
  const isSuperAdminUser = user?.role === "super_admin";
  const resolvedTab: ContentTab =
    SUPER_ADMIN_ONLY_TABS.has(requestedTab) && !isSuperAdminUser ? "chat" : requestedTab;
  // Hide top tab bar whenever user is in ORGANIC view (sidebar is the navigation).
  const isOrganicView = readViewMode() === "organic";
  const hideTopTabBar = isOrganicView;

  const handleTabClick = (tab: ContentTab) => {
    if (tab === "chat") {
      setOverrideTab(null);
      setCurrentModule("content-creator");
      setCurrentChatId(null);
      setLocation("/chat/content-creator");
    } else {
      setOverrideTab(tab);
    }
  };

  // Crosspost handoff: Analytics imported a reel's media into the store and now wants
  // the Scheduler composer. organicTab (sidebar-driven) outranks overrideTab, so update
  // whichever source is currently steering the active tab.
  const switchToScheduler = useCallback(() => {
    setOverrideTab("scheduler");
    if (organicTabValid) setOrganicTab("scheduler");
  }, [organicTabValid, setOrganicTab]);

  const handleSubModuleClick = (subModule: ChatSubModule) => {
    setCurrentModule(subModule);
    setCurrentChatId(null);
    setLocation(`/chat/${subModule}`);
  };

  const activeChatSub: ChatSubModule = CONTENT_TAB_MODULES.has(currentModule)
    ? (currentModule as ChatSubModule)
    : "content-creator";

  // Resizable carousel panel
  const [carouselPanelWidth, setCarouselPanelWidth] = useState(420);
  const isDragging = useRef(false);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      const startX = e.clientX;
      const startWidth = carouselPanelWidth;

      const handleMouseMove = (ev: MouseEvent) => {
        if (!isDragging.current) return;
        const delta = startX - ev.clientX;
        const newWidth = Math.max(300, Math.min(startWidth + delta, window.innerWidth * 0.75));
        setCarouselPanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [carouselPanelWidth]
  );

  const visibleTabs = TABS.filter(
    (tab) => !SUPER_ADMIN_ONLY_TABS.has(tab.id) || user?.role === "super_admin"
  );
  // First 6 tabs are always visible; rest collapse into "More" dropdown on narrow screens.
  const PRIMARY_TAB_COUNT = 6;
  const primaryTabs = visibleTabs.slice(0, PRIMARY_TAB_COUNT);
  const overflowTabs = visibleTabs.slice(PRIMARY_TAB_COUNT);
  const activeTabIsOverflow = overflowTabs.some((t) => t.id === resolvedTab);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const renderTabButton = (tab: (typeof TABS)[number]) => {
    const isActive = resolvedTab === tab.id;
    return (
      <button
        key={tab.id}
        onClick={() => {
          handleTabClick(tab.id);
          setMoreMenuOpen(false);
        }}
        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
          isActive
            ? "border-[#ef3e29] text-foreground bg-foreground/[0.04]"
            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]"
        }`}
      >
        {tab.icon}
        {t(tab.labelKey)}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab bar — hidden in ORGANIC mode (sidebar replaces it). */}
      {!hideTopTabBar && (
        <div className="flex items-center gap-1 px-4 pt-3 pb-0 shrink-0 border-b border-border overflow-x-auto">
          {primaryTabs.map(renderTabButton)}
          {/* Tabs 7+ shown inline on lg, hidden on smaller screens */}
          <div className="hidden lg:contents">{overflowTabs.map(renderTabButton)}</div>
          {/* Overflow dropdown on screens smaller than lg */}
          {overflowTabs.length > 0 && (
            <div className="relative lg:hidden">
              <button
                onClick={() => setMoreMenuOpen((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                  activeTabIsOverflow
                    ? "border-[#ef3e29] text-foreground bg-foreground/[0.04]"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]"
                }`}
              >
                {activeTabIsOverflow
                  ? t(overflowTabs.find((o) => o.id === resolvedTab)!.labelKey)
                  : "Więcej"}
                <span className="text-[8px]">▾</span>
              </button>
              {moreMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMoreMenuOpen(false)} />
                  <div className="absolute right-0 top-full z-20 mt-1 min-w-[200px] rounded-lg border border-border bg-popover py-1 shadow-2xl shadow-black/20">
                    {overflowTabs.map((tab) => {
                      const isActive = resolvedTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            handleTabClick(tab.id);
                            setMoreMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-left transition-colors ${
                            isActive
                              ? "bg-[#ef3e29]/15 text-foreground"
                              : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
                          }`}
                        >
                          {tab.icon}
                          {t(tab.labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {resolvedTab === "chat" && (
          <>
            {/* Sub-module navigation — hidden in ORGANIC view (sidebar replaces it). */}
            {!isOrganicView && (
              <div className="flex items-center gap-1 px-4 py-2 shrink-0 border-b border-border bg-foreground/[0.01]">
                {CHAT_SUB_MODULES.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => handleSubModuleClick(sub.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-all ${
                      activeChatSub === sub.id
                        ? "bg-foreground/[0.08] text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]"
                    }`}
                  >
                    {sub.icon}
                    {t(sub.labelKey)}
                  </button>
                ))}
              </div>
            )}
            {/* Chat content */}
            <div className="flex-1 overflow-hidden">
              <ModuleErrorBoundary moduleName="Chat AI">
                {activeChatSub === "carousel-creator" ? (
                  <div className="flex h-full">
                    <div className="flex-1 min-w-0 overflow-hidden border-r border-white/[0.06]">
                      <ChatInterface />
                    </div>
                    {/* Drag handle */}
                    <div
                      onMouseDown={handleDragStart}
                      className="w-1.5 shrink-0 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50 transition-colors bg-white/[0.04]"
                    />
                    <div
                      className="shrink-0 overflow-hidden bg-[#0a0c10]"
                      style={{ width: carouselPanelWidth }}
                    >
                      <CarouselSidePanel />
                    </div>
                  </div>
                ) : (
                  <ChatInterface />
                )}
              </ModuleErrorBoundary>
            </div>
          </>
        )}
        {resolvedTab === "graphics" && (
          <div className="flex-1 overflow-hidden px-6 py-4">
            <AdGraphicsBuilder initialPrompt={pendingGraphicPrompt} contentType="organic" />
          </div>
        )}
        {resolvedTab === "library" && (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <GraphicsLibrary
              contentType="organic"
              forUserId={forUserId}
              onEditPrompt={(prompt) => {
                setPendingGraphicPrompt(prompt);
                setOverrideTab("graphics");
              }}
            />
          </div>
        )}
        {resolvedTab === "pipeline" && (
          <ModuleErrorBoundary moduleName="Pipeline">
            <ContentPipeline />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "scheduler" && (
          <ModuleErrorBoundary moduleName="Scheduler">
            <ContentScheduler />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "calendar" && (
          <ModuleErrorBoundary moduleName="Kalendarz">
            <SchedulerCalendar />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "montaz" && (
          <ModuleErrorBoundary moduleName={t("contentCreator.tab.montage")}>
            <VideoEditor />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "generator" && (
          <ModuleErrorBoundary moduleName="Generator wideo">
            <VideoGenerator />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "motion-graphics" && (
          <ModuleErrorBoundary moduleName="Motion Graphics">
            <MotionGraphicsStudio />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "inbox" && (
          <ModuleErrorBoundary moduleName="Inbox">
            <SocialInbox />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "automations" && (
          <ModuleErrorBoundary moduleName="Asystent wiadomości">
            <SocialAutomations />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "analytics" && (
          <ModuleErrorBoundary moduleName="Analityka">
            <ContentAnalytics onCrosspostRequested={switchToScheduler} />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "inspiracje" && (
          <ModuleErrorBoundary moduleName="Inspiracje">
            <DailyViralInspiracje />
          </ModuleErrorBoundary>
        )}
        {resolvedTab === "highlights" && (
          <div className="flex-1 overflow-hidden">
            <YoutubeHighlight />
          </div>
        )}
      </div>
    </div>
  );
}
